import i18next, {t} from 'i18next';
import React from 'react';
import {ZodIssue} from 'zod';
import {toast} from 'react-toastify';
import {IValidationErrorObject, IValidationErrorResponse} from '../incase-domain/common/validationErrorResponse';
import {ISlide} from '../incase-domain/slide';
import i18n, {type TypedTranslation} from '../i18n';

export function generateErrorMessages(errors: IValidationErrorObject | null, field: string): JSX.Element[] | null {
    if (errors === null) return null;

    const errorMessages = getErrorMessages(errors, field);
    if (!errorMessages) return null;

    return errorMessages.map((message) => {
        return (
            <div key={message} className="l-group__item_error">
                {composeValidationMessage(message)}
            </div>
        );
    });
}

export function getErrorClasses(errors: IValidationErrorObject | null, field: string): string {
    return hasErrors(errors, field) ? 'is-error' : '';
}

function hasErrors(errors: IValidationErrorObject | null, field: string): boolean {
    if (!errors) return false;

    const fields = field.split('.');
    let currentObj: IValidationErrorObject | string[] | string | undefined = errors;

    for (let i = 0; i < fields.length; i++) {
        const fieldName = fields[i];
        const isLastLoop = i + 1 >= fields.length;

        if (!currentObj || typeof currentObj === 'string') return false;

        if (isLastLoop) {
            if (!Array.isArray(currentObj[fieldName])) return false;
            return currentObj[fieldName].length > 0;
        }
        currentObj = currentObj[fieldName] && currentObj[fieldName][0];
    }

    return false;
}

function getErrorMessages(errors: IValidationErrorObject | null, field: string): null | string[] {
    if (!errors) return null;

    const fields = field.split('.');
    let currentObj: IValidationErrorObject | string[] | string | undefined = errors;
    let result: string[] = [];

    for (let i = 0; i < fields.length; i++) {
        const fieldName = fields[i];
        const isLastLoop = i + 1 >= fields.length;

        if (!currentObj || typeof currentObj === 'string') return null;

        if (isLastLoop) {
            if (!Array.isArray(currentObj[fieldName])) return null;
            if (currentObj[fieldName].length <= 0) return null;
            if (typeof currentObj[fieldName][0] !== 'string') return null;

            result = currentObj[fieldName] as unknown as string[];
        } else {
            currentObj = currentObj[fieldName] && currentObj[fieldName][0];
        }
    }

    return result;
}

export const composeValidationMessage = (message: string): string => {
    if (!message.includes(';')) return i18n.t(message as TypedTranslation);

    const [field, type, value] = message.split(';');
    return `${i18n.t(`validation.field.${field}` as TypedTranslation)} ${i18next.t(`validation.type.${type}` as never, {value}) as string}`;
};

// Format zod errors to a format that can be used in existing global error handling
export const formatErrors = (errors: ZodIssue[]): IValidationErrorObject => {
    return errors.reduce((acc, error) => {
        return updateErrorObject(acc, error.path, error.message);
    }, {});
};

const updateErrorObject = (obj: IValidationErrorObject, path: (string | number)[], value: string): IValidationErrorObject => {
    const [head, ...tail] = path;
    const isLast = tail.length === 0;

    if (isLast) {
        const existing = obj[head];
        if (Array.isArray(existing)) {
            return {...obj, [head]: [...existing, value]} as IValidationErrorObject;
        }

        return {...obj, [head]: [value]} as IValidationErrorObject;
    }

    const currentValue = obj[head] as IValidationErrorObject[];
    const nestedObj = currentValue && currentValue[0] ? currentValue[0] : {};

    return {
        ...obj,
        [head]: [updateErrorObject(nestedObj, tail, value)],
    };
};

export function mapElementErrorsToIds(errorData: IValidationErrorResponse, slide: ISlide): IValidationErrorResponse {
    const elementErrors = errorData.errors.elements?.[0];
    if (!elementErrors || typeof elementErrors !== 'object' || Array.isArray(elementErrors)) {
        return errorData;
    }

    const mappedElementErrors: IValidationErrorObject = {};
    Object.entries(elementErrors).forEach(([key, val]) => {
        const id = Number.isSafeInteger(+key) && slide.elements[+key]?.id;
        if (id) {
            mappedElementErrors[id] = val;
        }
    });

    const newErrors = JSON.parse(JSON.stringify(errorData));
    newErrors.errors.elements = [mappedElementErrors];
    return newErrors;
}

export const handleValidationErrors = (errors: IValidationErrorObject | Record<string, string[]>): void => {
    const processErrors = (obj: IValidationErrorObject | Record<string, string[]>): void => {
        if (Array.isArray(obj)) {
            if (obj.every((item) => typeof item === 'string')) {
                obj.forEach((error) => toast.error(t(error)));
                return;
            }
            obj.forEach((item) => processErrors(item));
            return;
        }

        if (obj && typeof obj === 'object') {
            Object.values(obj).forEach((value) => processErrors(value));
        }
    };

    processErrors(errors);
};
